import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/** Hosts the Paddle.js overlay checkout for a prepared transaction (?_ptxn=txn_...). */
export default function PaddleCheckout() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const txnId = new URLSearchParams(window.location.search).get("_ptxn");

  useEffect(() => {
    if (!txnId) { setError("Missing transaction reference."); return; }
    let cancelled = false;

    const boot = async () => {
      try {
        const { data } = await api.get("/billing/paddle/config");
        if (cancelled) return;
        const openCheckout = () => {
          const Paddle = window.Paddle;
          if (data.environment === "sandbox") Paddle.Environment.set("sandbox");
          Paddle.Initialize({
            token: data.client_token,
            eventCallback: (ev) => {
              if (ev?.name === "checkout.completed") {
                setTimeout(() => navigate(`/payment-success?provider=paddle&session_id=${txnId}`), 1500);
              }
              if (ev?.name === "checkout.closed") {
                navigate("/dashboard/billing?tab=plan");
              }
            },
          });
          const locale = (navigator.language || "en").replace(/_/g, "-").split("@")[0];
          Paddle.Checkout.open({
            transactionId: txnId,
            settings: { displayMode: "overlay", locale },
          });
        };
        if (window.Paddle) { openCheckout(); return; }
        const s = document.createElement("script");
        s.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
        s.onload = openCheckout;
        s.onerror = () => setError("Could not load the Paddle checkout script.");
        document.body.appendChild(s);
      } catch (e) {
        setError(e.response?.data?.detail || "Paddle is not configured.");
      }
    };
    boot();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txnId]);

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-6" data-testid="paddle-checkout-page">
      <div className="text-center">
        {error ? (
          <>
            <div className="text-rose-400 font-semibold mb-2" data-testid="paddle-checkout-error">{error}</div>
            <button
              onClick={() => navigate("/dashboard/billing?tab=plan")}
              className="text-sm text-brand-amber hover:underline"
              data-testid="paddle-checkout-back"
            >
              Back to Billing
            </button>
          </>
        ) : (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-brand-amber mx-auto mb-3" />
            <div className="text-ink-secondary text-sm">Opening secure Paddle checkout…</div>
          </>
        )}
      </div>
    </div>
  );
}
